/**
 * Pure helpers for `useExperienceManager` (conformance, code-size guard).
 * Extracted verbatim from `use-experience-manager.tsx` so the hook file
 * keeps only React state + orchestration. Nothing here calls a React hook;
 * every export is a plain data-in/data-out function the hook composes.
 *
 * The canonical add/edit/delete flow (server-action calls, org-draft
 * creation, RHF selection writes) stays in the hook — there is no parallel
 * field list, schema, or mapper (single edit surface, Critical Rule #8).
 */
import type { ExperienceRow } from '../services/experience.service';
import {
  AWARD_CATEGORY_TYPES,
  EXPERIENCE_CATEGORY_TYPES,
  isFormType,
  type Experience,
  type ExperienceType,
  type FormType,
  type Organization,
  type Rank,
} from './experience-step-helpers';
import type { ExperienceStepMode } from './experience-step.types';

/**
 * Maps a DB-typed ExperienceRow to the UI-domain Experience type.
 *
 * The only structural difference is `legacy_data`: the DB type carries `Json`
 * (which is `string | number | boolean | null | Json[] | { [key: string]: Json }`)
 * while the UI type expects `Record<string, unknown> | null | undefined`.
 * At runtime the stored value is always an object or null, so the cast is safe.
 */
export function rowToExperience(row: ExperienceRow): Experience {
  return {
    ...row,
    legacy_data: row.legacy_data as Record<string, unknown> | null | undefined,
  };
}

export interface PartitionedExperiences {
  experienceList: Experience[];
  awardList: Experience[];
  affiliationList: Experience[];
  pastList: Experience[];
}

/**
 * Filters `experiences` by `filterTypes` (when provided) then partitions the
 * result into the four category buckets the UI renders.
 */
export function partitionExperiences(
  experiences: Experience[],
  filterTypes: ReadonlyArray<string> | undefined,
): PartitionedExperiences {
  const filterMatches = (type: string | null | undefined) => {
    if (!filterTypes || filterTypes.length === 0) return true;
    return type != null && filterTypes.includes(type);
  };

  const allFiltered = experiences.filter((e) => filterMatches(e.type));

  return {
    experienceList: allFiltered.filter(
      (e) => e.type != null && EXPERIENCE_CATEGORY_TYPES.includes(e.type),
    ),
    awardList: allFiltered.filter(
      (e) => e.type != null && AWARD_CATEGORY_TYPES.includes(e.type),
    ),
    affiliationList: allFiltered.filter(
      (e) => !!e.current && (e.type === 'school' || e.type === 'company'),
    ),
    pastList: allFiltered.filter((e) => !e.current && e.type !== 'award'),
  };
}

export interface EditFormState {
  selectedOrg: Organization | null;
  manualOrgName: string;
  formType: FormType;
  isAwardMode: boolean;
  selectedRank: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isWinner: boolean;
  placement: string;
  awardYear: string;
  awardTitle: string;
}

/**
 * Pure derivation of the inline-form field values for editing an existing
 * experience. `handleEditExperience` applies each field via its setState
 * setter and owns the editingId/showAddForm/scroll side effects.
 */
export function deriveEditFormState(exp: Experience): EditFormState {
  const monthSlice = (d: string | null | undefined) => (d ? d.slice(0, 7) : '');

  // Resolve org from existing data: prefer the linked organization, fall
  // back to manual name.
  const orgState =
    exp.organization && exp.organization_id
      ? {
          selectedOrg: {
            id: exp.organization_id,
            name: exp.organization.name,
            organization_type: exp.organization.organization_type ?? undefined,
          },
          manualOrgName: '',
        }
      : {
          selectedOrg: null,
          manualOrgName: exp.organization_manual ?? '',
        };

  // Type — default to 'school' for legacy NULL-type rows. Awards use their
  // own form-type slot so the rich card renders the placement input instead
  // of the rank selector.
  const expType = exp.type ?? 'school';
  const resolved: FormType = isFormType(expType)
    ? expType
    : expType === 'competition'
      ? 'other'
      : 'school';

  return {
    ...orgState,
    formType: resolved,
    isAwardMode: resolved === 'award',
    selectedRank: exp.rank_id ?? '',
    startDate: monthSlice(exp.start_date),
    endDate: monthSlice(exp.end_date),
    isCurrent: !!exp.current,
    isWinner: exp.rank_title === 'winner',
    placement: exp.placement ?? '',
    awardYear: exp.start_date ? exp.start_date.slice(0, 4) : '',
    awardTitle: resolved === 'award' ? (exp.title ?? '') : '',
  };
}

/**
 * Month-input (`YYYY-MM`) to DATE-column value (`YYYY-MM-01`). The DB
 * columns are DATE, so submitting a bare month needs day=01 to satisfy the
 * `experiences_dates_day_one` CHECK constraint. Returns `null` for anything
 * that doesn't match the expected month shape.
 */
export function monthInputToDate(m: string): string | null {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(m) ? `${m}-01` : null;
}

export interface AwardPayloadInput {
  selectedOrg: Organization | null;
  manualOrgName: string;
  placement: string;
  awardYear: string;
  awardTitle: string;
  /**
   * Overrides the persisted `organization_manual_type`. Set to `'achievement'`
   * when the typed competition name tripped the institution-vs-achievement
   * classifier and was routed to free text instead of an org row (org faucet,
   * phase 2). Defaults to `'competition'` — the award form's convention.
   */
  manualOrgType?: string | null;
}

/**
 * Builds the create/update payload for the award submit path. Caller is
 * responsible for the compName/placement validation before calling this.
 *
 * Like {@link buildExperiencePayload}, the competition name is persisted on
 * `organization_manual` as a plain-text display fallback (BAL-720): the
 * award path now also creates a draft org (`published=false`) at selection
 * time, which the public read join drops — so without a persisted manual
 * name the competition/award would render blank on the public profile and
 * partners' panel. Prefer the dancer's typed name, else the created org's
 * display name.
 */
export function buildAwardPayload({
  selectedOrg,
  manualOrgName,
  placement,
  awardYear,
  awardTitle,
  manualOrgType,
}: AwardPayloadInput) {
  const yearNum = awardYear.trim() ? Number.parseInt(awardYear, 10) : null;
  const startDateValue =
    yearNum && !Number.isNaN(yearNum) ? `${yearNum}-01-01` : null;

  const displayName = manualOrgName.trim() || (selectedOrg?.name ?? '');

  return {
    organization_id: selectedOrg?.id ?? null,
    organization_manual: displayName || null,
    organization_manual_type: displayName
      ? (manualOrgType ?? 'competition')
      : null,
    type: 'award' as ExperienceType,
    rank_id: null,
    rank_title: null,
    placement: placement.trim(),
    title: awardTitle.trim() || null,
    start_date: startDateValue,
    end_date: startDateValue,
    current: false,
    graduated: false,
    verified: false,
    hide_on_resume: false,
    hidden_on_candidate: false,
  };
}

/** True when the award form's compName/placement are non-empty after trim. */
export function isAwardSubmissionValid(
  selectedOrg: Organization | null,
  manualOrgName: string,
  placement: string,
): boolean {
  const compName = selectedOrg?.name || manualOrgName.trim();
  return !!compName && !!placement.trim();
}

/** True when the standard (school/company/other) form has an org name. */
export function isExperienceSubmissionValid(
  selectedOrg: Organization | null,
  manualOrgName: string,
): boolean {
  return !!(selectedOrg?.name || manualOrgName.trim());
}

/** Rank category bucket for a given form type — 'school' or 'company'. */
export function rankCategoryForFormType(
  formType: FormType,
): 'school' | 'company' {
  return formType === 'school' ? 'school' : 'company';
}

/** Filters a rank list down to the given category. */
export function filterRanksByCategory(
  ranks: Rank[],
  category: 'school' | 'company',
): Rank[] {
  return ranks.filter((r) => r.category === category);
}

export interface AutoOpenFormParams {
  mode: ExperienceStepMode;
  hasProfile: boolean;
  isLoading: boolean;
  showAddForm: boolean;
  dismissedByUser: boolean;
  filterTypes: ReadonlyArray<string> | undefined;
  experiences: Experience[];
}

/**
 * Decides whether the inline experience form should auto-open on empty
 * state (profile mode only). Most dancers arrive with no entries; making
 * them click an extra button to start typing is wasted friction. Awards
 * stay collapsed — too presumptuous for dancers without competition
 * history. Returns `false` for select modes, while loading, once the form
 * is already open/dismissed, once the dancer has a non-award experience,
 * when `filterTypes` is restricted to awards only, or with no profile yet.
 */
export function shouldAutoOpenExperienceForm({
  mode,
  hasProfile,
  isLoading,
  showAddForm,
  dismissedByUser,
  filterTypes,
  experiences,
}: AutoOpenFormParams): boolean {
  if (mode !== 'profile') return false;
  if (!hasProfile) return false;
  if (isLoading) return false;
  if (showAddForm) return false;
  if (dismissedByUser) return false;
  if (
    filterTypes &&
    filterTypes.length > 0 &&
    filterTypes.every((ft) => AWARD_CATEGORY_TYPES.includes(ft))
  ) {
    return false;
  }
  const hasNonAwardExperience = experiences.some((e) => e.type !== 'award');
  return !hasNonAwardExperience;
}

export interface ExperiencePayloadInput {
  resolvedOrgId: string | null;
  editingId: string | null;
  submittedSelectedOrg: Organization | null;
  submittedManualName: string;
  submittedFormType: FormType;
  selectedRank: string;
  isWinner: boolean;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  /**
   * Overrides the persisted `organization_manual_type`. Set to `'achievement'`
   * when the typed name tripped the institution-vs-achievement classifier and
   * was routed to free text instead of minting an org row (org faucet, phase
   * 2). Defaults to the form type when unset.
   */
  manualOrgType?: string | null;
}

/**
 * Builds the create/update payload for the standard (school/company/other)
 * submit path.
 *
 * `organization_manual` display-name fallback (BAL-720): the org a dancer
 * picks/creates is now resolved eagerly at selection time via
 * `OrganizationPicker`'s `onCreate` — a freshly-typed company name becomes
 * an UNVERIFIED DRAFT org (`published=false`) and `selectedOrg` is populated
 * by submit time (so `submittedManualName` is empty). The public read join
 * (`pickPublicOrg`) requires `published===true`, so it drops the draft →
 * any viewer other than the creator + super admins (public profile,
 * competition partners' candidate panel, resume PDF) would render an empty
 * org name — the "Other / Other" regression. To keep the company name
 * visible we persist a plain-text fallback on `organization_manual` for NEW
 * rows: the dancer's typed name when present, else the selected/created
 * org's display name. (Pre-BAL-248 the submit path always persisted the
 * typed name; the draft-org-at-selection refactor silently dropped it
 * because `submittedSelectedOrg` is now always set.)
 */
export function buildExperiencePayload({
  resolvedOrgId,
  editingId,
  submittedSelectedOrg,
  submittedManualName,
  submittedFormType,
  selectedRank,
  isWinner,
  startDate,
  endDate,
  isCurrent,
  manualOrgType,
}: ExperiencePayloadInput) {
  // Display name to persist as the fallback: the dancer's free-text name
  // when they typed one, otherwise the selected/created org's name.
  const displayName =
    submittedManualName.length > 0
      ? submittedManualName
      : (submittedSelectedOrg?.name ?? '');
  const persistManual = !editingId && displayName.length > 0;

  return {
    organization_id: resolvedOrgId,
    organization_manual: persistManual ? displayName : null,
    organization_manual_type: persistManual
      ? (manualOrgType ?? submittedFormType)
      : null,
    type: submittedFormType as ExperienceType,
    rank_id: selectedRank || null,
    rank_title: isWinner && submittedFormType === 'other' ? 'winner' : null,
    start_date: startDate ? monthInputToDate(startDate) : null,
    end_date: isCurrent ? null : endDate ? monthInputToDate(endDate) : null,
    current: isCurrent,
    graduated: submittedFormType === 'school' && !isCurrent,
    verified: false,
    hide_on_resume: false,
    hidden_on_candidate: false,
  };
}
